/**
 * StimaRicostruzioneTab - Fase 10C.1
 *
 * Tab admin per CRM stime ricostruzione: lista + filtri + dettaglio modal +
 * ricalcola + cambio status + creazione manuale.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Plus, RefreshCw, Calculator, Edit3, Loader2, X,
  AlertTriangle, Clock, MapPin, User, Mail, Tag,
} from "lucide-react";
import StimaCostoRicostruzioneWizard from "@/components/common/StimaCostoRicostruzioneWizard";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_OPTS = [
  { v: "",                    l: "Tutti",         color: "bg-zinc-100" },
  { v: "nuova",                l: "Nuove",         color: "bg-blue-100 text-blue-800" },
  { v: "in_lavorazione",       l: "In lavorazione", color: "bg-yellow-100 text-yellow-800" },
  { v: "contattato",           l: "Contattato",    color: "bg-purple-100 text-purple-800" },
  { v: "convertita_polizza",   l: "Convertito",    color: "bg-emerald-100 text-emerald-800" },
  { v: "scartata",             l: "Scartato",      color: "bg-zinc-200 text-zinc-700" },
];

function fmtEuro(v) {
  return `€ ${Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
}

export default function StimaRicostruzioneTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selected, setSelected] = useState(null); // detail modal
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/valore-ricostruzione/lista`, {
        withCredentials: true, params: { status: filterStatus || undefined, page, limit },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Errore caricamento stime");
    } finally { setLoading(false); }
  }, [filterStatus, page]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/api/admin/valore-ricostruzione/${id}`, { withCredentials: true });
      setSelected(r.data);
    } catch (_e) { toast.error("Errore caricamento dettaglio"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl text-[#163321] flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Stime Costo Ricostruzione
          </h2>
          <p className="text-xs text-zinc-500">Lead self-service + stime manuali — totale {total}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-1.5 text-sm border rounded-lg inline-flex items-center gap-1">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Ricarica
          </button>
          <button onClick={() => setShowWizard(true)} className="px-3 py-1.5 text-sm bg-[#163321] text-white rounded-lg inline-flex items-center gap-1" data-testid="stima-tab-new-manual">
            <Plus className="w-4 h-4" /> Nuova stima manuale
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap mb-4">
        {STATUS_OPTS.map(o => (
          <button key={o.v} onClick={() => { setFilterStatus(o.v); setPage(1); }} className={`px-3 py-1 text-xs rounded-full border transition ${filterStatus === o.v ? "bg-[#163321] text-white border-[#163321]" : "bg-white border-zinc-200 hover:border-zinc-300"}`}>
            {o.l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-left px-3 py-2">Cliente</th>
              <th className="text-left px-3 py-2">Comune</th>
              <th className="text-left px-3 py-2">Tipologia</th>
              <th className="text-right px-3 py-2">Polizza consigliata</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-zinc-400">Nessuna stima trovata</td></tr>
            )}
            {items.map((it, i) => {
              const opt = STATUS_OPTS.find(o => o.v === it.status) || { color: "", l: it.status };
              return (
                <tr key={it.id || i} onClick={() => openDetail(it.id)} className="hover:bg-zinc-50 cursor-pointer border-t border-zinc-100" data-testid="stima-tab-row">
                  <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">{(it.created_at || "").substring(0, 10)}</td>
                  <td className="px-3 py-2">{it.contatto?.nome} {it.contatto?.cognome}<div className="text-xs text-zinc-400">{it.contatto?.email}</div></td>
                  <td className="px-3 py-2">{it.geo?.comune} <span className="text-xs text-zinc-400">({it.geo?.provincia})</span></td>
                  <td className="px-3 py-2 text-xs">{it.input_immobile?.tipologia} <span className="text-zinc-400">{it.input_immobile?.superficie_mq}mq</span></td>
                  <td className="px-3 py-2 text-right font-mono">{fmtEuro(it.calcolo?.valore_per_polizza_consigliato)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${opt.color}`}>{opt.l}</span></td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${it.source === "admin_manual" ? "bg-violet-100 text-violet-800" : "bg-cyan-100 text-cyan-800"}`}>{it.source === "admin_manual" ? "Admin" : "Self"}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-end items-center gap-2 mt-3 text-sm">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1 border rounded disabled:opacity-40">‹</button>
        <span className="text-xs text-zinc-500">Pag. {page} / {totalPages}</span>
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-2 py-1 border rounded disabled:opacity-40">›</button>
      </div>

      {/* Detail modal */}
      {selected && <StimaDetailModal stima={selected} onClose={() => setSelected(null)} onChanged={() => { load(); }} />}
      {showWizard && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto py-8 px-4">
          <div className="max-w-3xl mx-auto relative">
            <button onClick={() => setShowWizard(false)} className="absolute -top-2 right-2 z-10 bg-white rounded-full p-2 shadow"><X className="w-4 h-4" /></button>
            <StimaCostoRicostruzioneWizard mode="admin" onComplete={() => { load(); setTimeout(() => setShowWizard(false), 500); }} onCancel={() => setShowWizard(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
function StimaDetailModal({ stima, onClose, onChanged }) {
  const [tab, setTab] = useState("riepilogo");
  const [busy, setBusy] = useState(false);
  const [statusVal, setStatusVal] = useState(stima.status);
  const [statusNote, setStatusNote] = useState("");
  const c = stima.calcolo || {};
  const reviews = c.needs_review_voci || [];
  const audit = stima.audit_log || [];

  const updateStatus = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/api/admin/valore-ricostruzione/${stima.id}/status`, {
        status: statusVal, nota: statusNote || undefined,
      }, { withCredentials: true });
      toast.success("Status aggiornato");
      setStatusNote("");
      onChanged();
    } catch (_e) { toast.error("Errore aggiornamento"); }
    finally { setBusy(false); }
  };

  const ricalcola = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/valore-ricostruzione/${stima.id}/ricalcola`, {
        applica_modulazione_geo: true,
      }, { withCredentials: true });
      toast.success(`Ricalcolato: € ${r.data.calcolo?.valore_per_polizza_consigliato?.toLocaleString("it-IT")}`);
      onChanged();
      onClose();
    } catch (_e) { toast.error("Errore ricalcolo"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-heading text-lg text-[#163321]">Stima ricostruzione — {stima.contatto?.nome} {stima.contatto?.cognome}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {reviews.length > 0 && (
          <div className="bg-amber-50 border-b border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> <span><strong>Stima provvisoria</strong> — {reviews.length} parametri marcati needs_review</span>
          </div>
        )}

        <div className="flex gap-2 p-3 border-b text-sm">
          {[
            {k:"riepilogo", l:"Riepilogo"},
            {k:"breakdown", l:"Breakdown"},
            {k:"geo",       l:"Profilo Geo"},
            {k:"audit",     l:"Audit log"},
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-1.5 rounded-lg ${tab === t.k ? "bg-[#163321] text-white" : "hover:bg-zinc-100"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="p-5 max-h-[60vh] overflow-auto">
          {tab === "riepilogo" && (
            <div>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div className="p-4 border rounded-xl"><div className="text-xs text-zinc-500">Min</div><div className="font-bold text-zinc-700 text-lg">{fmtEuro(c.valore_per_polizza_minimo)}</div></div>
                <div className="p-4 border-2 border-[#163321] rounded-xl bg-[#163321]/5"><div className="text-xs text-[#163321]">Polizza consigliata</div><div className="font-bold text-[#163321] text-2xl">{fmtEuro(c.valore_per_polizza_consigliato)}</div></div>
                <div className="p-4 border rounded-xl"><div className="text-xs text-zinc-500">Max</div><div className="font-bold text-zinc-700 text-lg">{fmtEuro(c.valore_per_polizza_massimo)}</div></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-sm mb-4">
                <Field label="Tipologia" value={c.tipologia} />
                <Field label="Superficie" value={`${c.superficie_mq} mq`} />
                <Field label="Volume" value={`${c.volume_mc} mc`} />
                <Field label="Costo netto medio" value={fmtEuro(c.costo_totale_netto_medio)} />
                <Field label="Costo lordo medio" value={fmtEuro(c.costo_totale_lordo_medio)} />
                <Field label="Moltiplicatore tot." value={c.moltiplicatore_totale} />
              </div>

              <h4 className="font-semibold text-sm mt-4 mb-2">Cambio status</h4>
              <div className="flex gap-2 items-end flex-wrap">
                <select value={statusVal} onChange={e => setStatusVal(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                  {STATUS_OPTS.filter(o => o.v).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <input type="text" placeholder="Nota (opz)" value={statusNote} onChange={e => setStatusNote(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                <button onClick={updateStatus} disabled={busy} className="px-4 py-2 bg-[#163321] text-white rounded-lg text-sm disabled:opacity-40">{busy ? "..." : "Aggiorna"}</button>
                <button onClick={ricalcola} disabled={busy} className="px-4 py-2 border rounded-lg text-sm inline-flex items-center gap-1"><Calculator className="w-4 h-4" /> Ricalcola</button>
              </div>
            </div>
          )}

          {tab === "breakdown" && (
            <div>
              <div className="text-xs text-zinc-500 mb-2">Costo unitario €/mq dopo modulazione: min {fmtEuro(c.costo_unitario_modulato_eur_mq?.min)} · medio {fmtEuro(c.costo_unitario_modulato_eur_mq?.medio)} · max {fmtEuro(c.costo_unitario_modulato_eur_mq?.max)}</div>
              <table className="w-full text-sm border">
                <thead className="bg-zinc-50"><tr><th className="text-left px-3 py-2">Voce</th><th className="px-3 py-2">Tipo</th><th className="text-right px-3 py-2">Fattore</th></tr></thead>
                <tbody>
                  {(c.coefficienti_applicati || []).map((co, i) => (
                    <tr key={i} className="border-t"><td className="px-3 py-1.5">{co.voce}</td><td className="px-3 py-1.5 text-center text-xs">{co.tipologia}</td><td className="px-3 py-1.5 text-right font-mono">{Number(co.fattore).toFixed(3)}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                <Field label="Spese tecniche" value={`${c.spese_tecniche?.percentuale || 0}% · ${fmtEuro(c.spese_tecniche?.importo)}`} />
                <Field label="Contributo costr." value={`${c.contributo_costruzione?.percentuale || 0}% · ${fmtEuro(c.contributo_costruzione?.importo)}`} />
                <Field label="IVA" value={`${c.iva?.aliquota || 0}% · ${fmtEuro(c.iva?.importo)}`} />
                <Field label="Lordo medio" value={fmtEuro(c.costo_totale_lordo_medio)} />
              </div>
              {reviews.length > 0 && (
                <details className="mt-4 text-xs"><summary className="cursor-pointer">Voci needs_review ({reviews.length})</summary>
                  <ul className="list-disc pl-5 text-zinc-500 mt-2">{reviews.map(r => <li key={r}>{r}</li>)}</ul>
                </details>
              )}
            </div>
          )}

          {tab === "geo" && (
            <div>
              {stima.geo?.profilo_rischio?.score_0_100 != null ? (
                <>
                  <div className="flex gap-3 mb-3 items-center">
                    <div className="text-3xl font-bold text-[#163321]">{stima.geo.profilo_rischio.score_0_100}</div>
                    <div className="text-sm text-zinc-500">Score rischio geo / 100<br /><span className="text-xs">{stima.geo.profilo_rischio.fonte}</span></div>
                  </div>
                  <pre className="bg-zinc-50 p-3 rounded text-xs overflow-auto">{JSON.stringify(stima.geo.profilo_rischio.breakdown, null, 2)}</pre>
                </>
              ) : <div className="text-zinc-400">Profilo geo non calcolato (modulazione non attivata).</div>}
            </div>
          )}

          {tab === "audit" && (
            <div className="space-y-2">
              {audit.length === 0 && <div className="text-zinc-400">Nessun evento.</div>}
              {audit.map((a, i) => (
                <div key={i} className="text-xs border-l-2 border-[#163321] pl-3 py-1">
                  <div className="text-zinc-500">{(a.ts || "").substring(0, 19)} · {a.actor}</div>
                  <div className="font-semibold">{a.action}</div>
                  {a.details && <pre className="text-zinc-500 mt-1">{JSON.stringify(a.details, null, 2)}</pre>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return <div><span className="text-zinc-500">{label}:</span> <strong>{value || "—"}</strong></div>;
}
